/**
 * RFC 7636 PKCE helpers — pure Web Crypto, zero deps.
 *
 * Used by the Tauri desktop flow (``useDesktopOAuth``) to bind the
 * authorization code to a per-attempt random secret the IdP never
 * sees in the clear. The web BFF flow uses a different mechanism
 * (server-side code/verifier round-trip), so this module is loaded
 * ONLY on the desktop path.
 *
 * - ``verifier`` — 32 random bytes, base64url-encoded (43 chars). Stays
 *   in memory until the code is exchanged, then immediately discarded.
 * - ``challenge`` — ``SHA-256(verifier)`` base64url-encoded. Sent on
 *   the ``/authorize`` request to Zitadel.
 * - ``state`` — 16 random bytes, base64url-encoded. Round-trips through
 *   the IdP and is checked against the original on the
 *   ``oauth-callback`` deep-link.
 */

const ENCODER = new TextEncoder();

/** RFC 4648 §5 base64url (URL-safe, no padding). */
function base64url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomBytes(length: number): Uint8Array {
  const buf = new Uint8Array(length);
  crypto.getRandomValues(buf);
  return buf;
}

export function generatePkceVerifier(): string {
  // 32 random bytes → 43 base64url chars — RFC 7636 §4.1 max allowed
  // length is 128, min is 43. Sticking to 43 keeps the URL short.
  return base64url(randomBytes(32));
}

export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", ENCODER.encode(verifier));
  return base64url(new Uint8Array(digest));
}

export function generateOAuthState(): string {
  // 16 random bytes is the bare minimum that survives the CSRF guard
  // (RFC 6749 §10.12) — 22 base64url chars.
  return base64url(randomBytes(16));
}

export interface PkcePair {
  readonly verifier: string;
  readonly challenge: string;
  readonly state: string;
}

export async function buildPkcePair(): Promise<PkcePair> {
  const verifier = generatePkceVerifier();
  const challenge = await deriveCodeChallenge(verifier);
  const state = generateOAuthState();
  return { verifier, challenge, state };
}
